<template>
  <div class="sub-platforms-editor">
    <p v-if="!value.length" class="sub-platforms-editor__empty">
      {{ $t('page.admin-platform-catalog.sub-platforms.empty') }}
    </p>

    <ul v-else class="sub-platforms-editor__list">
      <li
        v-for="(sub, index) in value"
        :key="sub._key"
        class="sub-platforms-editor__item"
        :class="{ 'is-open': isOpen(sub), 'has-error': showErrors && rowHasError(index) }"
      >
        <div class="sub-platforms-editor__row">
          <button
            type="button"
            class="sub-platforms-editor__toggle"
            :aria-expanded="String(isOpen(sub))"
            @click="toggle(sub)"
          >
            <BaseIcon :name="isOpen(sub) ? 'chevron-down' : 'chevron-right'" />
            <span class="sub-platforms-editor__name">{{ displayName(sub) }}</span>
            <code v-if="sub.id" class="sub-platforms-editor__id">{{ sub.id }}</code>
            <BaseTag v-for="region in sub.regions" :key="region.code" tone="neutral" preset="soft">
              {{ region.code }}
            </BaseTag>
            <BaseTag v-if="sub._isNew" tone="info" preset="soft">
              {{ $t('page.admin-platform-catalog.sub-platforms.new') }}
            </BaseTag>
          </button>
          <div class="sub-platforms-editor__row-actions">
            <BaseRoundButton
              :size="32"
              icon="arrow-up"
              is-transparent
              :disabled="index === 0"
              :tooltip-text="$t('page.admin-platform-catalog.sub-platforms.move-up')"
              @click.native="move(index, -1)"
            />
            <BaseRoundButton
              :size="32"
              icon="arrow-down"
              is-transparent
              :disabled="index === value.length - 1"
              :tooltip-text="$t('page.admin-platform-catalog.sub-platforms.move-down')"
              @click.native="move(index, 1)"
            />
            <BaseRoundButton
              :size="32"
              icon="trash-2"
              is-transparent
              is-red
              :tooltip-text="$t('page.admin-platform-catalog.sub-platforms.remove')"
              @click.native="remove(index)"
            />
          </div>
        </div>

        <div v-if="isOpen(sub)" class="sub-platforms-editor__fields">
          <FormInput
            has-gray-background
            :name="`${sub._key}-id`"
            :label="$t('page.admin-platform-catalog.fields.id')"
            :value="sub.id"
            :readonly="!sub._isNew"
            :hint="sub._isNew ? $t('page.admin-platform-catalog.sub-platforms.id-hint') : null"
            :error="fieldError(index, 'id')"
            is-required
            @input="update(index, { id: $event })"
          />
          <FormInput
            has-gray-background
            :name="`${sub._key}-url`"
            :label="$t('page.admin-platform-catalog.fields.url')"
            :value="sub.url"
            placeholder="https://"
            :error="fieldError(index, 'url')"
            @input="update(index, { url: $event })"
          />
          <FormInput
            has-gray-background
            :name="`${sub._key}-name-fr`"
            :label="$t('page.admin-platform-catalog.fields.name-fr')"
            :value="sub.nameFr"
            :error="fieldError(index, 'nameFr')"
            is-required
            @input="update(index, { nameFr: $event })"
          />
          <FormInput
            has-gray-background
            :name="`${sub._key}-name-en`"
            :label="$t('page.admin-platform-catalog.fields.name-en')"
            :value="sub.nameEn"
            :error="fieldError(index, 'nameEn')"
            is-required
            @input="update(index, { nameEn: $event })"
          />
          <FormInput
            has-gray-background
            :name="`${sub._key}-description-fr`"
            :label="$t('page.admin-platform-catalog.fields.description-fr')"
            :value="sub.descriptionFr"
            is-textarea
            rows="3"
            @input="update(index, { descriptionFr: $event })"
          />
          <FormInput
            has-gray-background
            :name="`${sub._key}-description-en`"
            :label="$t('page.admin-platform-catalog.fields.description-en')"
            :value="sub.descriptionEn"
            is-textarea
            rows="3"
            @input="update(index, { descriptionEn: $event })"
          />
          <div class="sub-platforms-editor__regions">
            <FormLabel>{{ $t('page.admin-platform-catalog.sections.regions') }}</FormLabel>
            <PlatformRegionsPicker
              :value="sub.regions"
              :hint="$t('page.admin-platform-catalog.regions-hint.sub-platform')"
              @change="update(index, { regions: $event })"
            />
          </div>
        </div>
      </li>
    </ul>

    <BaseButton class="sub-platforms-editor__add" is-small is-transparent has-icon-left @click.native="add">
      <BaseIcon name="plus" />
      {{ $t('page.admin-platform-catalog.sub-platforms.add') }}
    </BaseButton>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import BaseTag from '@/components/base/BaseTag';
import FormInput from '@/components/form/FormInput';
import FormLabel from '@/components/form/FormLabel';
import PlatformRegionsPicker from '@/components/page-admin-platform-catalog/PlatformRegionsPicker';
import { LOCALE } from '@/util/i18n/locale';

let nextKey = 0;

/**
 * The platform's sub-platforms as collapsed rows that open into a form. `value` is the draft list
 * (each row carries a client-only `_key`, and `_isNew` until saved); every change emits `input`
 * with a new list. An existing sub-platform's id is read-only, the way the platform's own id is.
 * `fieldErrors` is validatePlatform()'s map; it shows once `showErrors` is set, and
 * openRowsWithErrors() opens the rows at fault.
 */
export default {
  name: 'SubPlatformsEditor',
  components: { BaseButton, BaseIcon, BaseRoundButton, BaseTag, FormInput, FormLabel, PlatformRegionsPicker },
  props: {
    value: { type: Array, default: () => [] },
    fieldErrors: { type: Object, default: () => ({}) },
    showErrors: { type: Boolean, default: false },
  },
  data() {
    return { openKeys: {} };
  },
  methods: {
    openRowsWithErrors() {
      this.value.forEach((sub, index) => {
        if (this.rowHasError(index)) this.$set(this.openKeys, sub._key, true);
      });
    },
    displayName(sub) {
      const name = this.$i18n.locale === LOCALE.FR_CA ? sub.nameFr : sub.nameEn;
      return name || sub.nameFr || sub.nameEn || this.$t('page.admin-platform-catalog.sub-platforms.untitled');
    },
    isOpen(sub) {
      return !!this.openKeys[sub._key];
    },
    toggle(sub) {
      this.$set(this.openKeys, sub._key, !this.openKeys[sub._key]);
    },
    rowHasError(index) {
      const prefix = `subPlatforms.${index}.`;
      return Object.keys(this.fieldErrors).some((path) => path.startsWith(prefix));
    },
    fieldError(index, field) {
      const error = this.showErrors && this.fieldErrors[`subPlatforms.${index}.${field}`];
      return error ? this.$t(`page.admin-platform-catalog.errors.${error}`) : null;
    },
    update(index, patch) {
      this.$emit(
        'input',
        this.value.map((sub, i) => (i === index ? { ...sub, ...patch } : sub)),
      );
    },
    move(index, offset) {
      const next = [...this.value];
      const [sub] = next.splice(index, 1);
      next.splice(index + offset, 0, sub);
      this.$emit('input', next);
    },
    remove(index) {
      this.$emit(
        'input',
        this.value.filter((_, i) => i !== index),
      );
    },
    add() {
      const sub = {
        _key: `new-sub-${nextKey++}`,
        _isNew: true,
        id: '',
        nameFr: '',
        nameEn: '',
        descriptionFr: '',
        descriptionEn: '',
        url: '',
        regions: [],
      };
      this.$set(this.openKeys, sub._key, true);
      this.$emit('input', [...this.value, sub]);
    },
  },
};
</script>

<style lang="scss">
.sub-platforms-editor {
  &__empty {
    margin: 0 0 12px;
    font-size: 13px;
    color: $color-slate-500;
  }

  &__list {
    list-style: none;
    margin: 0 0 12px;
    padding: 0;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    background: $color-white;
  }

  &__item {
    & + & {
      border-top: 1px solid $color-border;
    }

    &.has-error {
      box-shadow: inset 3px 0 0 $color-error;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px 4px 4px;
  }

  &__toggle {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: 8px;
    padding: 6px;
    border: none;
    border-radius: $radius-sm;
    background: none;
    cursor: pointer;
    font: inherit;
    text-align: left;
    color: $color-normal;

    &:hover {
      background: $color-slate-50;
    }

    &:focus-visible {
      outline: none;
      box-shadow: $shadow-focus;
    }

    .feather {
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      color: $color-slate-400;
    }
  }

  &__name {
    overflow: hidden;
    font-size: 13px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__id {
    flex: 0 0 auto;
    padding: 0 4px;
    border-radius: $radius-sm;
    background: $color-slate-50;
    color: $color-slate-500;
    font-size: 11px;
  }

  &__row-actions {
    display: flex;
    flex: 0 0 auto;
    gap: 2px;
  }

  &__fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 16px;
    padding: 0 16px 16px 34px;

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }

  &__regions {
    grid-column: 1 / -1;
    padding-top: 8px;

    .form-label {
      display: block;
      margin-bottom: 6px;
    }
  }
}
</style>
